import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Globe, Shield, Scale, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { practiceAreas, stats, testimonials } from '../utils/mock';
import InsightsGrid from '../components/InsightsGrid';
import PageTitle from '../hooks/PageTitle';

const Home = () => {
  const featuredPracticeAreas = practiceAreas.slice(0, 3);

  return (
    <div className="min-h-screen">
      <PageTitle title="Home" />
      {/* Hero Section with Logo Watermark */}
      <section className="relative h-screen flex items-center">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Logo Watermark Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <div className="text-white/15 text-[20rem] font-serif font-bold tracking-tighter">
              Ashbourne
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-serif text-white mb-6 leading-tight">
              Corporate, M&A and<br />Cross-Border Legal Excellence
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Sophisticated legal solutions for corporations and high net worth individuals across borders
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-base font-medium"
              >
                <Link to="/contact">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-base font-medium"
              >
                <Link to="/practice-areas">
                  Our Expertise
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-serif mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-400">
                  {stat.label}
                  {stat.badge && (
                    <span className="ml-2 inline-block px-2 py-1 text-xs bg-blue-600 text-white rounded-full">
                      {stat.badge}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Jurisdictions List */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <h3 className="text-center text-sm uppercase tracking-wider text-gray-400 mb-4">Our Jurisdictions</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['England', 'Luxembourg', 'Switzerland', 'Dubai'].map((jurisdiction, idx) => (
                <span key={idx} className="px-4 py-2 bg-gray-800 rounded-lg text-sm">
                  {jurisdiction}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
                A Boutique Firm with Global Reach
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Ashbourne Law is a premier international law firm with headquarters in London and offices in major financial centres worldwide. We provide sophisticated legal counsel to corporations, financial institutions, and high net worth individuals.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team combines deep expertise in corporate, commercial, and cross-border transactions with a commitment to delivering pragmatic, business-focused solutions.
              </p>
              <Button asChild variant="outline" size="lg" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
                <Link to="/about">
                  Learn More About Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="relative h-96 lg:h-full min-h-[500px]">
              <img
                src="https://images.pexels.com/photos/6077091/pexels-photo-6077091.jpeg"
                alt="Legal excellence"
                className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Our Practice Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive legal services across multiple jurisdictions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {featuredPracticeAreas.map((area) => {
              const IconComponent = {
                Building2,
                Briefcase: Globe,
                Globe,
                FileText: Scale
              }[area.icon] || Building2;

              return (
                <Card key={area.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gray-900 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-serif text-gray-900 mb-3">{area.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{area.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-gray-900 text-white hover:bg-gray-800">
              <Link to="/practice-areas">
                View All Practice Areas
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Why Choose Ashbourne
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Proven Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                Over 30 years of combined board experience delivering exceptional results for our clients across complex international matters
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Global Network</h3>
              <p className="text-gray-600 leading-relaxed">
                Offices in England, Luxembourg, Switzerland, and Dubai 
                <span className="inline-block ml-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                  and expanding
                </span>
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Dedicated Service</h3>
              <p className="text-gray-600 leading-relaxed">
                Partner-led teams providing personalized attention and strategic counsel to every client
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif mb-4">
              Client Testimonials
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-gray-800 border-0">
                <CardContent className="p-8">
                  <div className="mb-4">
                    <svg className="w-10 h-10 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                  <p className="text-sm text-gray-400">— {testimonial.client}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Insights & Perspectives Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Insights & Perspectives
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert analysis and thought leadership on corporate law, M&A, and cross-border transactions
            </p>
          </div>

          <InsightsGrid />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
            Ready to Discuss Your Legal Needs?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contact our team for a confidential consultation
          </p>
          <Button asChild size="lg" className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-6 text-base">
            <Link to="/contact">
              Get In Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
